import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Printer, FileImage, File, FileText, ExternalLink, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { JournalEntry, JournalAttachment } from "@/types/journal";
import { formatDate } from "@/utils/dateFormat";
import { toast } from "react-hot-toast";

const JournalEntryView = () => {
  const { entryId } = useParams();
  const navigate = useNavigate();
  const [entry, setEntry] = useState<JournalEntry | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [userName, setUserName] = useState<string>("");
  const [attachments, setAttachments] = useState<JournalAttachment[]>([]);
  const [loadingAttachments, setLoadingAttachments] = useState(false);
  const [loadingStates, setLoadingStates] = useState<Record<string, boolean>>({});

  useEffect(() => {
    const fetchEntryAndUser = async () => {
      try {
        // Fetch the journal entry
        const { data: entryData, error: entryError } = await supabase
          .from('journal_entries')
          .select('*')
          .eq('id', entryId)
          .single();

        if (entryError) throw entryError;
        setEntry(entryData as JournalEntry);

        // Fetch the user's name
        const { data: userData, error: userError } = await supabase
          .from('user_profiles')
          .select('first_name, last_name')
          .eq('user_id', entryData.user_id)
          .single();

        if (userError) throw userError;
        setUserName(`${userData.first_name || ''} ${userData.last_name || ''}`);

        // Fetch attachments
        setLoadingAttachments(true);
        const { data: attachmentsData, error: attachmentsError } = await supabase.rpc('get_journal_attachments', {
          entry_id: entryId
        });

        if (attachmentsError) throw attachmentsError;
        setAttachments(attachmentsData as JournalAttachment[]);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load journal entry');
      } finally {
        setLoading(false);
        setLoadingAttachments(false);
      }
    };

    if (entryId) {
      fetchEntryAndUser();
    }
  }, [entryId]);

  const handleViewAttachment = async (attachment: JournalAttachment) => {
    try {
      setLoadingStates(prev => ({ ...prev, [attachment.id]: true }));
      
      const { data } = await supabase.storage
        .from('journal_attachments')
        .createSignedUrl(attachment.file_path, 60 * 5); // 5 minutes
      
      if (data?.signedUrl) {
        window.open(data.signedUrl, '_blank');
      } else {
        toast.error('Failed to generate file URL');
      }
    } catch (error) {
      console.error('Error generating file URL:', error);
      toast.error('Failed to open file');
    } finally {
      setLoadingStates(prev => ({ ...prev, [attachment.id]: false }));
    }
  };

  const getFileIcon = (mimeType: string) => {
    if (mimeType.startsWith('image/')) {
      return <FileImage className="h-5 w-5" />;
    } else if (mimeType === 'application/pdf') {
      return <File className="h-5 w-5" />;
    } else if (mimeType === 'text/plain') {
      return <FileText className="h-5 w-5" />;
    } else {
      return <File className="h-5 w-5" />;
    }
  };

  const handlePrint = () => {
    const printContent = document.getElementById('print-content');
    if (printContent) {
      const originalContent = document.body.innerHTML;
      const printWindow = window.open('', '_blank');
      
      if (printWindow) {
        printWindow.document.write(`
          <html>
            <head>
              <title>Journal Entry - ${userName}</title>
              <style>
                body { font-family: Arial, sans-serif; padding: 20px; }
                .header { margin-bottom: 20px; }
                .content { white-space: pre-wrap; }
                .metadata { color: #666; margin-bottom: 20px; }
                @media print {
                  body { padding: 0; }
                  .no-print { display: none; }
                }
              </style>
            </head>
            <body>
              <div class="header">
                <h1>Journal Entry</h1>
                <p class="metadata">
                  <strong>Author:</strong> ${userName}<br>
                  <strong>Date:</strong> ${formatDate(entry?.created_at || '')}<br>
                  ${entry?.emotion_type ? `<strong>Emotion:</strong> ${entry.emotion_type} (Intensity: ${entry.emotion_intensity}/10)<br>` : ''}
                  ${entry?.mood ? `<strong>Mood:</strong> ${entry.mood}` : ''}
                </p>
              </div>
              <div class="content">
                ${entry?.content || ''}
              </div>
            </body>
          </html>
        `);
        
        printWindow.document.close();
        printWindow.focus();
        printWindow.print();
        printWindow.close();
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-[#3BAFDA]" />
      </div>
    );
  }

  if (error || !entry) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Card className="max-w-2xl mx-auto">
          <CardHeader>
            <CardTitle className="text-red-600">Error</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground mb-4">{error || 'Entry not found'}</p>
            <Button onClick={() => navigate('/journal/all')}>Back to Journal</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const isNegativeEmotion = ['Sad', 'Angry', 'Frustrated', 'Anxious', 'Guilty', 'Overwhelmed'].includes(entry.emotion_type || '');

  return (
    <div className="px-4 py-8">
      <div className="flex justify-between items-center mb-4">
        <Button
          variant="ghost"
          onClick={() => navigate('/journal/all')}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Journal
        </Button>
        <Button
          variant="outline"
          onClick={handlePrint}
          className="flex items-center"
        >
          <Printer className="h-4 w-4 mr-2" />
          Print Entry
        </Button>
      </div>

      <Card className="max-w-4xl mx-auto">
        <CardHeader className={`${isNegativeEmotion ? 'bg-red-50' : 'bg-[#E8F0F8]'} rounded-t-lg p-6`}>
          <div className="flex flex-col md:flex-row justify-between items-start gap-4">
            <div className="text-left">
              <p className="text-sm text-muted-foreground mb-2">
                Journal Entry by {userName} • {formatDate(entry.created_at)}
              </p>
              <CardTitle className={`text-2xl md:text-3xl ${isNegativeEmotion ? 'text-red-700' : 'text-[#333B44]'}`}>
                {entry.mood || "How are you feeling?"}
              </CardTitle>
            </div>
            {entry.emotion_type && (
              <div className="text-right">
                <p className={`text-lg font-medium ${isNegativeEmotion ? 'text-red-600' : 'text-[#3BAFDA]'}`}>
                  {entry.emotion_type}
                </p>
                <p className="text-sm text-muted-foreground">
                  Intensity: {entry.emotion_intensity}/10
                </p>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-6">
          <div className="prose max-w-none prose-lg">
            <p className="whitespace-pre-wrap text-base md:text-lg leading-relaxed">{entry.content}</p>
          </div>
          {entry.media_url && (
            <div className={`mt-8 rounded-lg overflow-hidden border ${isNegativeEmotion ? 'border-red-100' : 'border-[#CCCCCC]'}`}>
              {entry.media_type === 'audio' ? (
                <audio controls src={entry.media_url} className="w-full" />
              ) : (
                <video 
                  controls 
                  src={entry.media_url} 
                  className="w-full max-h-[500px] h-auto rounded-lg object-contain"
                />
              )}
            </div>
          )}
          
          {/* Display attachments only if they exist */}
          {loadingAttachments ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="h-5 w-5 animate-spin text-[#3BAFDA]" />
              <span className="ml-2 text-sm text-[#333B44]">Loading attachments...</span>
            </div>
          ) : attachments.length > 0 && (
            <div className={`mt-8 border ${isNegativeEmotion ? 'border-red-100' : 'border-[#CCCCCC]'} rounded-lg p-4 bg-white`}>
              <h3 className="text-lg font-medium mb-4">Attached Files</h3>
              <div className="space-y-3">
                {attachments.map((attachment) => (
                  <div key={attachment.id} className="flex items-center justify-between bg-white p-3 rounded-md border border-[#CCCCCC]">
                    <div className="flex items-center">
                      <span className="text-[#3BAFDA] mr-3">
                        {getFileIcon(attachment.mime_type)}
                      </span>
                      <div>
                        <p className="text-sm font-medium truncate max-w-[200px] sm:max-w-[300px]">
                          {attachment.filename}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {(attachment.file_size / 1024).toFixed(1)} KB
                        </p>
                      </div>
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => handleViewAttachment(attachment)}
                      disabled={loadingStates[attachment.id]}
                      className="h-8 w-8 p-0"
                    >
                      {loadingStates[attachment.id] ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <ExternalLink className="h-4 w-4" />
                      )}
                      <span className="sr-only">View</span>
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default JournalEntryView; 